import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { STALE_BUILD_DETECTION_FEATURE } from "constants/feature-flags";
import { getAppVersion } from "utils/lazy-with-retry.util";

import useSyncBetweenTabs, {
  SyncMessage,
  dispatchTabSyncMessage
} from "./use-sync-between-tabs.hook";

const VERSION_MANIFEST_URL = "/version.json";

// Backstop only — visibilitychange and route-change are what actually catch a deploy
// promptly. Jittered per mount so open tabs don't all poll in the same instant after
// one goes live (see the module doc below).
const POLL_INTERVAL_MS = 15 * 60 * 1000;

const STALE_BUILD_DETECTED_ACTION = "stale_build_detected";
const STALE_BUILD_DISMISSED_ACTION = "stale_build_dismissed";

interface DetectedPayload {
  sha: string;
  builtAt: string;
  buildNumber: number | null;
  minSupportedBuildNumber: number | null;
}

interface DismissedPayload {
  sha: string;
}

export interface BuildVersionState {
  /** True once a build newer than this tab's own has been seen and not yet dismissed. */
  isStale: boolean;
  latestSha: string | null;
  builtAt: string | null;
  latestBuildNumber: number | null;
  minSupportedBuildNumber: number | null;
  /** Suppresses the pill for `latestSha` only — a later deploy (a different sha) re-arms it. */
  dismiss: () => void;
}

/**
 * ENG-747 Phase A: polls the static /version.json manifest (no backend involvement — see
 * that ticket's Revision History) to detect that this tab is running a stale build.
 *
 * Three triggers, per the design brief:
 * - Route change (also covers the first check for free: the effect below runs once on
 *   mount in addition to every `location.pathname` change).
 * - `visibilitychange` → visible (a backgrounded tab that regains focus checks immediately).
 * - A jittered 15-minute interval, but the poll only actually fires while the document is
 *   visible — a hidden tab that never becomes visible again should never poll.
 *
 * Only the currently-visible tab fetches; every tab (visible or not) needs to render the
 * result, so all tabs — including the one that polled — listen on the same BroadcastChannel
 * used elsewhere in the app (`use-sync-between-tabs.hook.ts`, the pattern behind the
 * patient-arrived banner's cross-tab dismiss sync). A hand-rolled second channel with its own
 * BroadcastChannel-unavailable fallback was considered and dropped: `use-sync-between-tabs`
 * already constructs `new BroadcastChannel(...)` unconditionally at module scope, so on a
 * browser without BroadcastChannel the whole app already fails to boot via that shared
 * module's existing consumers (e.g. the patient-arrived banner) — a fallback scoped to only
 * this feature would never get a chance to run.
 *
 * Entirely inert when `STALE_BUILD_DETECTION_FEATURE` is off: no fetch, no listeners, no
 * interval, no BroadcastChannel traffic.
 */
export default function useBuildVersionCheck(): BuildVersionState {
  const flags = useFlags();
  const enabled = Boolean(flags[STALE_BUILD_DETECTION_FEATURE]);
  const location = useLocation();
  const currentSha = getAppVersion();

  const [latestSha, setLatestSha] = useState<string | null>(null);
  const [builtAt, setBuiltAt] = useState<string | null>(null);
  const [latestBuildNumber, setLatestBuildNumber] = useState<number | null>(
    null
  );
  const [minSupportedBuildNumber, setMinSupportedBuildNumber] = useState<
    number | null
  >(null);
  const [dismissedSha, setDismissedSha] = useState<string | null>(null);

  const isFetchingRef = useRef(false);
  const processedMessageIds = useRef(new Set<string>());

  const applyDetected = useCallback(
    (detected: DetectedPayload) => {
      // A manifest that matches our own build is nothing to report — guards both a
      // same-build poll response and a stray/duplicate broadcast.
      if (detected.sha === currentSha) return;
      setLatestSha((prev) => (prev === detected.sha ? prev : detected.sha));
      setBuiltAt(detected.builtAt);
      setLatestBuildNumber(detected.buildNumber);
      setMinSupportedBuildNumber(detected.minSupportedBuildNumber);
    },
    [currentSha]
  );

  const poll = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      // Failed fetches are silent no-ops (network blip, offline, the file 404ing
      // mid-deploy) — never surface UI or throw for a background check.
      let manifest: DetectedPayload | null = null;
      try {
        const response = await fetch(VERSION_MANIFEST_URL, {
          cache: "no-store"
        });
        if (response.ok) {
          const data = await response.json();
          if (typeof data?.sha === "string") {
            manifest = {
              sha: data.sha,
              builtAt: data.builtAt,
              buildNumber: data.buildNumber ?? null,
              minSupportedBuildNumber: data.minSupportedBuildNumber ?? null
            };
          }
        }
      } catch {
        manifest = null;
      }
      if (!manifest) return;

      applyDetected(manifest);
      if (manifest.sha !== currentSha) {
        dispatchTabSyncMessage({
          action: STALE_BUILD_DETECTED_ACTION,
          payload: manifest
        });
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [applyDetected, currentSha]);

  // Trigger: route change (and the initial check, for free — this effect also runs once
  // on mount). Explicitly gated on visibility too: a route change in a hidden tab (e.g. a
  // programmatic redirect while backgrounded) shouldn't poll.
  useEffect(() => {
    if (!enabled || document.visibilityState !== "visible") return;
    poll();
  }, [enabled, location.pathname, poll]);

  // Trigger: visibilitychange → visible.
  useEffect(() => {
    if (!enabled) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [enabled, poll]);

  // Trigger: jittered 15-minute backstop. One jittered interval per mount — recomputing
  // it every tick isn't necessary, spreading tabs apart once is enough to avoid a
  // thundering herd right after a deploy.
  useEffect(() => {
    if (!enabled) return;
    const jitteredIntervalMs = POLL_INTERVAL_MS * (1 + Math.random() * 0.2);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") poll();
    }, jitteredIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [enabled, poll]);

  const handleSyncMessage = useCallback(
    (message: SyncMessage) => {
      if (message.action === STALE_BUILD_DETECTED_ACTION) {
        const payload = message.payload as DetectedPayload | undefined;
        if (payload?.sha) applyDetected(payload);
        return;
      }
      if (message.action === STALE_BUILD_DISMISSED_ACTION) {
        const payload = message.payload as DismissedPayload | undefined;
        if (payload?.sha) setDismissedSha(payload.sha);
      }
    },
    [applyDetected]
  );

  useSyncBetweenTabs(processedMessageIds, handleSyncMessage, !enabled);

  const dismiss = useCallback(() => {
    if (!latestSha) return;
    setDismissedSha(latestSha);
    dispatchTabSyncMessage({
      action: STALE_BUILD_DISMISSED_ACTION,
      payload: { sha: latestSha }
    });
  }, [latestSha]);

  return {
    isStale: latestSha !== null && latestSha !== dismissedSha,
    latestSha,
    builtAt,
    latestBuildNumber,
    minSupportedBuildNumber,
    dismiss
  };
}
